import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { FileText, Users, Edit, Eye, Building2, Search, X, Save, Calendar, User, Phone, Mail, DollarSign, CreditCard, CheckCircle, XCircle, AlertCircle, Receipt, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Contratos = () => {
  const { user } = useAuth();
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExtrato, setShowExtrato] = useState(false);
  const [extratoContrato, setExtratoContrato] = useState(null);
  const [extrato, setExtrato] = useState(null);
  const [extratoLoading, setExtratoLoading] = useState(false);
  const [salvandoLancamento, setSalvandoLancamento] = useState(false);
  const [lancamento, setLancamento] = useState({
    competencia: '',
    valor: '',
    data_vencimento: '',
    data_pagamento: '',
    status: 'pendente',
    observacao: ''
  });
  const [formData, setFormData] = useState({
    quantidade_unidades: '',
    quantidade_maxima_usuarios: '',
    tipo_cobranca: '',
    forma_pagamento: '',
    status: 'ativo',
    vencimento: '',
    responsavel_cobranca: '',
    contato: '',
    email: '',
    observacoes: ''
  });

  useEffect(() => {
    if (user?.tipo === 'administrador') {
      loadContratos();
    }
  }, [user]);

  const loadContratos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contratos');
      setContratos(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
      toast.error('Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const loadContratoDetails = async (condominioId) => {
    try {
      const response = await api.get(`/contratos/${condominioId}`);
      const data = response.data.data;
      
      const vencimento = data.vencimento ? data.vencimento.split('T')[0] : '';
      
      const newFormData = {
        quantidade_unidades: data.quantidade_unidades?.toString() || '',
        quantidade_maxima_usuarios: data.quantidade_maxima_usuarios?.toString() || '',
        tipo_cobranca: data.tipo_cobranca || '',
        forma_pagamento: data.forma_pagamento || '',
        status: data.status || 'ativo',
        vencimento: vencimento,
        responsavel_cobranca: data.responsavel_cobranca || '',
        contato: data.contato ? applyPhoneMask(data.contato) : '',
        email: data.email || '',
        observacoes: data.observacoes || ''
      };
      
      // Se tipo_cobranca for único e não tiver vencimento, preencher com data fictícia
      if (data.tipo_cobranca === 'unico' && !vencimento) {
        newFormData.vencimento = '9999-12-12';
      }
      
      setFormData(newFormData);
    } catch (error) {
      console.error('Erro ao carregar detalhes do contrato:', error);
      toast.error('Erro ao carregar detalhes do contrato');
    }
  };

  const handleView = async (contrato) => {
    setSelectedContrato(contrato);
    setIsEditing(false);
    await loadContratoDetails(contrato.condominio_id);
    setShowDetailModal(true);
  };

  const handleEdit = async (contrato) => {
    setSelectedContrato(contrato);
    setIsEditing(true);
    await loadContratoDetails(contrato.condominio_id);
    setShowDetailModal(true);
  };

  const handleClose = () => {
    setShowDetailModal(false);
    setSelectedContrato(null);
    setIsEditing(false);
    setFormData({
      quantidade_unidades: '',
      quantidade_maxima_usuarios: '',
      tipo_cobranca: '',
      forma_pagamento: '',
      status: 'ativo',
      vencimento: '',
      responsavel_cobranca: '',
      contato: '',
      observacoes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload = {
        quantidade_unidades: formData.quantidade_unidades ? parseInt(formData.quantidade_unidades, 10) : null,
        quantidade_maxima_usuarios: formData.quantidade_maxima_usuarios ? parseInt(formData.quantidade_maxima_usuarios) : null,
        tipo_cobranca: formData.tipo_cobranca || null,
        forma_pagamento: formData.forma_pagamento || null,
        status: formData.status,
        vencimento: formData.tipo_cobranca === 'unico' ? '9999-12-12' : (formData.vencimento || null),
        responsavel_cobranca: formData.responsavel_cobranca || null,
        contato: formData.contato ? formData.contato.replace(/\D/g, '') : '', // Remove máscara antes de enviar
        email: formData.email || null,
        observacoes: formData.observacoes || null
      };

      await api.put(`/contratos/${selectedContrato.condominio_id}`, payload);
      toast.success('Contrato atualizado com sucesso!');
      handleClose();
      loadContratos();
    } catch (error) {
      console.error('Erro ao atualizar contrato:', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar contrato');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredContratos = contratos.filter(contrato =>
    contrato.condominio_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contrato.condominio_cidade.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'inativo':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'suspenso':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'cancelado':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ativo':
        return <CheckCircle className="h-4 w-4" />;
      case 'inativo':
        return <XCircle className="h-4 w-4" />;
      case 'suspenso':
        return <AlertCircle className="h-4 w-4" />;
      case 'cancelado':
        return <XCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'ativo': 'Ativo',
      'inativo': 'Inativo',
      'suspenso': 'Suspenso',
      'cancelado': 'Cancelado'
    };
    return statusMap[status] || status;
  };

  // Função para aplicar máscara de telefone
  const formatarMoeda = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatarData = (valor) => {
    if (!valor) return '—';
    const texto = String(valor).slice(0, 10);
    const [ano, mes, dia] = texto.split('-');
    if (!ano || !mes || !dia) return texto;
    return `${dia}/${mes}/${ano}`;
  };

  const formatarCompetencia = (valor) => {
    if (!valor || !String(valor).includes('-')) return valor || '—';
    const [ano, mes] = String(valor).split('-');
    return `${mes}/${ano}`;
  };

  const limparLancamento = () => {
    setLancamento({
      competencia: '',
      valor: '',
      data_vencimento: '',
      data_pagamento: '',
      status: 'pendente',
      observacao: ''
    });
  };

  const carregarExtrato = async (condominioId) => {
    const response = await api.get(`/contratos/${condominioId}/pagamentos`);
    setExtrato(response.data.data);
  };

  const handleAbrirExtrato = async (contrato) => {
    setExtratoContrato(contrato);
    setShowExtrato(true);
    setExtrato(null);
    limparLancamento();
    try {
      setExtratoLoading(true);
      await carregarExtrato(contrato.condominio_id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar o extrato');
    } finally {
      setExtratoLoading(false);
    }
  };

  const handleLancarPagamento = async () => {
    if (!extratoContrato) return;
    if (!lancamento.competencia || !lancamento.valor) {
      toast.error('Informe a competência e o valor.');
      return;
    }
    try {
      setSalvandoLancamento(true);
      await api.post(`/contratos/${extratoContrato.condominio_id}/pagamentos`, {
        competencia: lancamento.competencia,
        valor: Number(String(lancamento.valor).replace(',', '.')),
        data_vencimento: lancamento.data_vencimento || null,
        data_pagamento: lancamento.data_pagamento || null,
        status: lancamento.status,
        observacao: lancamento.observacao || null
      });
      toast.success('Lançamento incluído no extrato');
      limparLancamento();
      await carregarExtrato(extratoContrato.condominio_id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao lançar pagamento');
    } finally {
      setSalvandoLancamento(false);
    }
  };

  const handleAtualizarLancamento = async (pagamento, status) => {
    try {
      await api.put(`/contratos/${extratoContrato.condominio_id}/pagamentos/${pagamento.contrato_pagamento_id}`, {
        status
      });
      await carregarExtrato(extratoContrato.condominio_id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar lançamento');
    }
  };

  const handleRemoverLancamento = async (pagamento) => {
    if (!window.confirm('Remover este lançamento do extrato?')) return;
    try {
      await api.delete(`/contratos/${extratoContrato.condominio_id}/pagamentos/${pagamento.contrato_pagamento_id}`);
      await carregarExtrato(extratoContrato.condominio_id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover lançamento');
    }
  };

  const applyPhoneMask = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara (XX) XXXXX-XXXX
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // Verificar se é administrador
  if (user?.tipo !== 'administrador') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Acesso negado. Apenas administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Controle de Contratos</h1>
        </div>
      </div>

      {/* Busca */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Buscar por condomínio ou cidade..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Tabela de Contratos - Grid Básico */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Condomínio
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unidades
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuários Ativos
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantidade Máxima
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContratos.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    Nenhum contrato encontrado
                  </td>
                </tr>
              ) : (
                filteredContratos.map((contrato) => (
                  <tr key={contrato.condominio_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {contrato.condominio_nome}
                          </div>
                          <div className="text-sm text-gray-500">
                            {contrato.condominio_cidade} - {contrato.condominio_estado}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-medium text-gray-900">
                        {contrato.quantidade_unidades ?? '—'}
                      </span>
                      {contrato.unidades_cadastradas != null && (
                        <div className="text-xs text-gray-500">{contrato.unidades_cadastradas} cadastradas</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {contrato.usuarios_ativos}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm text-gray-900">
                        {contrato.quantidade_maxima !== null ? contrato.quantidade_maxima : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(contrato.status)}`}>
                        {getStatusIcon(contrato.status)}
                        {getStatusText(contrato.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleAbrirExtrato(contrato)}
                          className="p-1 text-indigo-600 hover:text-indigo-800"
                          title="Extrato de pagamento"
                        >
                          <Receipt className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleView(contrato)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                          title="Visualizar"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(contrato)}
                          className="p-1 text-green-600 hover:text-green-800"
                          title="Editar"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumo */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-blue-600 font-medium">Total de Condomínios</div>
          <div className="text-2xl font-bold text-blue-900">{contratos.length}</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-green-600 font-medium">Total de Usuários Ativos</div>
          <div className="text-2xl font-bold text-green-900">
            {contratos.reduce((sum, c) => sum + c.usuarios_ativos, 0)}
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm text-purple-600 font-medium">Contratos Ativos</div>
          <div className="text-2xl font-bold text-purple-900">
            {contratos.filter(c => c.status === 'ativo').length}
          </div>
        </div>
      </div>

      {/* Modal de Detalhes/Edição */}
      {showDetailModal && selectedContrato && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {isEditing ? 'Editar Contrato' : 'Detalhes do Contrato'}
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informações do Condomínio */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Condomínio</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Nome</label>
                    <p className="text-sm font-medium text-gray-900">{selectedContrato.condominio_nome}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">CNPJ</label>
                    <p className="text-sm font-medium text-gray-900">{selectedContrato.condominio_cnpj}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Cidade</label>
                    <p className="text-sm font-medium text-gray-900">{selectedContrato.condominio_cidade}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Estado</label>
                    <p className="text-sm font-medium text-gray-900">{selectedContrato.condominio_estado}</p>
                  </div>
                </div>
              </div>

              {/* Formulário */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade de unidades
                    </label>
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        value={formData.quantidade_unidades}
                        onChange={(e) => setFormData({ ...formData, quantidade_unidades: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ex.: 120"
                      />
                    ) : (
                      <p className="text-sm text-gray-900">{formData.quantidade_unidades || '—'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade Máxima de Usuários <span className="text-red-500">*</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        value={formData.quantidade_maxima_usuarios}
                        onChange={(e) => setFormData({ ...formData, quantidade_maxima_usuarios: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-sm text-gray-900">{formData.quantidade_maxima_usuarios || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status <span className="text-red-500">*</span>
                    </label>
                    {isEditing ? (
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                        <option value="suspenso">Suspenso</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    ) : (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(formData.status)}`}>
                        {getStatusIcon(formData.status)}
                        {getStatusText(formData.status)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <DollarSign className="h-4 w-4 inline mr-1" />
                      Tipo de Cobrança
                    </label>
                    {isEditing ? (
                      <select
                        value={formData.tipo_cobranca}
                        onChange={(e) => {
                          const newTipoCobranca = e.target.value;
                          setFormData({ 
                            ...formData, 
                            tipo_cobranca: newTipoCobranca,
                            vencimento: newTipoCobranca === 'unico' ? '9999-12-12' : formData.vencimento
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Selecione...</option>
                        <option value="mensal">Mensal</option>
                        <option value="trimestral">Trimestral</option>
                        <option value="semestral">Semestral</option>
                        <option value="anual">Anual</option>
                        <option value="unico">Único</option>
                      </select>
                    ) : (
                      <p className="text-sm text-gray-900">{formData.tipo_cobranca || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <CreditCard className="h-4 w-4 inline mr-1" />
                      Forma de Pagamento
                    </label>
                    {isEditing ? (
                      <select
                        value={formData.forma_pagamento}
                        onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Selecione...</option>
                        <option value="boleto">Boleto</option>
                        <option value="pix">PIX</option>
                        <option value="transferencia">Transferência</option>
                        <option value="cartao_credito">Cartão de Crédito</option>
                        <option value="cartao_debito">Cartão de Débito</option>
                        <option value="dinheiro">Dinheiro</option>
                        <option value="outro">Outro</option>
                      </select>
                    ) : (
                      <p className="text-sm text-gray-900">{formData.forma_pagamento || '-'}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Calendar className="h-4 w-4 inline mr-1" />
                      Vencimento <span className="text-red-500">*</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={formData.tipo_cobranca === 'unico' ? '9999-12-12' : formData.vencimento}
                        onChange={(e) => {
                          if (formData.tipo_cobranca !== 'unico') {
                            setFormData({ ...formData, vencimento: e.target.value });
                          }
                        }}
                        disabled={formData.tipo_cobranca === 'unico'}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formData.tipo_cobranca === 'unico' ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      />
                    ) : (
                      <p className="text-sm text-gray-900">
                        {formData.vencimento && formData.vencimento !== '9999-12-12' 
                          ? new Date(formData.vencimento).toLocaleDateString('pt-BR') 
                          : formData.tipo_cobranca === 'unico' ? 'Pagamento Único' : '-'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="h-4 w-4 inline mr-1" />
                      Responsável pela Cobrança <span className="text-red-500">*</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.responsavel_cobranca}
                        onChange={(e) => setFormData({ ...formData, responsavel_cobranca: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        maxLength={100}
                      />
                    ) : (
                      <p className="text-sm text-gray-900">{formData.responsavel_cobranca || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Phone className="h-4 w-4 inline mr-1" />
                      Contato <span className="text-red-500">*</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.contato}
                        onChange={(e) => {
                          const maskedValue = applyPhoneMask(e.target.value);
                          setFormData({ ...formData, contato: maskedValue });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        maxLength={15}
                        placeholder="(XX) XXXXX-XXXX"
                      />
                    ) : (
                      <p className="text-sm text-gray-900">{formData.contato || '-'}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Email <span className="text-gray-500 text-xs font-normal">(opcional)</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      maxLength={100}
                      placeholder="email@exemplo.com (opcional)"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{formData.email || '-'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  {isEditing ? (
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{formData.observacoes || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer do Modal */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {isEditing ? 'Cancelar' : 'Fechar'}
              </button>
              {isEditing && (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              )}
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Editar
                </button>
              )}
              <button
                onClick={() => handleAbrirExtrato(selectedContrato)}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Receipt className="h-4 w-4" />
                Extrato
              </button>
            </div>
          </div>
        </div>
      )}

      {showExtrato && extratoContrato && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Extrato de pagamento</h2>
                <p className="text-sm text-gray-500">{extratoContrato.condominio_nome}</p>
              </div>
              <button onClick={() => setShowExtrato(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-green-700">Total pago</div>
                  <div className="text-xl font-bold text-green-900">{formatarMoeda(extrato?.total_pago)}</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="text-sm text-amber-700">Total pendente</div>
                  <div className="text-xl font-bold text-amber-900">{formatarMoeda(extrato?.total_pendente)}</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-blue-700">Unidades do contrato</div>
                  <div className="text-xl font-bold text-blue-900">{extratoContrato.quantidade_unidades ?? '—'}</div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Novo lançamento</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Competência</label>
                    <input
                      type="month"
                      value={lancamento.competencia}
                      onChange={(e) => setLancamento({ ...lancamento, competencia: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Valor</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={lancamento.valor}
                      onChange={(e) => setLancamento({ ...lancamento, valor: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Status</label>
                    <select
                      value={lancamento.status}
                      onChange={(e) => setLancamento({ ...lancamento, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="pendente">Pendente</option>
                      <option value="pago">Pago</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Vencimento</label>
                    <input
                      type="date"
                      value={lancamento.data_vencimento}
                      onChange={(e) => setLancamento({ ...lancamento, data_vencimento: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Data do pagamento</label>
                    <input
                      type="date"
                      value={lancamento.data_pagamento}
                      onChange={(e) => setLancamento({ ...lancamento, data_pagamento: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Observação</label>
                    <input
                      type="text"
                      value={lancamento.observacao}
                      onChange={(e) => setLancamento({ ...lancamento, observacao: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleLancarPagamento}
                    disabled={salvandoLancamento}
                    className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {salvandoLancamento ? 'Salvando...' : 'Incluir no extrato'}
                  </button>
                </div>
              </div>

              {extratoLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Competência</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimento</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pagamento</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(extrato?.pagamentos || []).length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-6 text-center text-gray-500">Nenhum lançamento neste contrato</td>
                        </tr>
                      ) : extrato.pagamentos.map((pagamento) => (
                        <tr key={pagamento.contrato_pagamento_id}>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatarCompetencia(pagamento.competencia)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatarMoeda(pagamento.valor)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatarData(pagamento.data_vencimento)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatarData(pagamento.data_pagamento)}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${
                              pagamento.status === 'pago'
                                ? 'text-green-700 bg-green-50 border-green-200'
                                : pagamento.status === 'cancelado'
                                  ? 'text-gray-600 bg-gray-50 border-gray-200'
                                  : 'text-amber-700 bg-amber-50 border-amber-200'
                            }`}>
                              {pagamento.status === 'pago' ? 'Pago' : pagamento.status === 'cancelado' ? 'Cancelado' : 'Pendente'}
                            </span>
                            {pagamento.observacao && (
                              <div className="text-xs text-gray-500 mt-1">{pagamento.observacao}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              {pagamento.status !== 'pago' && (
                                <button
                                  type="button"
                                  onClick={() => handleAtualizarLancamento(pagamento, 'pago')}
                                  className="text-sm text-green-700 hover:underline"
                                >
                                  Dar baixa
                                </button>
                              )}
                              {pagamento.status === 'pago' && (
                                <button
                                  type="button"
                                  onClick={() => handleAtualizarLancamento(pagamento, 'pendente')}
                                  className="text-sm text-amber-700 hover:underline"
                                >
                                  Reabrir
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoverLancamento(pagamento)}
                                className="text-red-600 hover:text-red-800"
                                title="Remover"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contratos;

